import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Snackbar,
  CircularProgress,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { ArrowBack, Save, Cancel } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { centerServiceRequisitionsService } from './centerServiceRequisitions.service';
import { diagnosticCentersService } from '../diagnosticCenters/diagnosticCenters.service';
import { servicesService } from '../services/services.service';
import { DiagnosticCenter } from '../diagnosticCenters/diagnosticCenters.dto';
import { Service } from '../services/services.dto';
import {
  RequisitionExamType,
  RequisitionOrderReason,
} from './centerServiceRequisitions.dto';
import {
  requisitionOptionsService,
  RequisitionOption,
} from './requisitionOptions.service';

const EditCenterServiceRequisition = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { token, logout } = useAuth();

  const [formData, setFormData] = useState({
    diagnosticCenterId: '',
    serviceId: '',
    examType: 'OTHER' as RequisitionExamType | string,
    examTypeOtherText: '',
    orderReason: 'OTHER' as RequisitionOrderReason | string,
    orderReasonOtherText: '',
    showBillingSection: false,
  });
  const [centers, setCenters] = useState<DiagnosticCenter[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [examTypes, setExamTypes] = useState<RequisitionOption[]>([]);
  const [orderReasons, setOrderReasons] = useState<RequisitionOption[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    centerServiceRequisitionsService.setLogoutCallback(logout);
    if (!token || !id) return;

    const load = async () => {
      try {
        const [centersRes, servicesRes, examTypesRes, orderReasonsRes, record] =
          await Promise.all([
            diagnosticCentersService.getDiagnosticCenters(token, {
              limit: 500,
            }),
            servicesService.getServices(token, { limit: 100 }),
            requisitionOptionsService.getExamTypes(token),
            requisitionOptionsService.getOrderReasons(token),
            centerServiceRequisitionsService.getRequisition(token, Number(id)),
          ]);
        setCenters(centersRes.data || []);
        // Inactive/unavailable services shouldn't be offered here - a
        // requisition can't sensibly be attached to a service that isn't
        // actually bookable. Still keep whichever service this requisition
        // is already assigned to in the list even if it's since gone
        // inactive/unavailable, so editing an existing requisition doesn't
        // silently lose its current selection.
        setServices(
          (servicesRes.data || []).filter(
            service =>
              (service.status === 1 && service.isAvailable === 1) ||
              service.id === record.serviceId
          )
        );
        setExamTypes(examTypesRes.filter(opt => opt.isActive) || []);
        setOrderReasons(orderReasonsRes.filter(opt => opt.isActive) || []);
        setFormData({
          diagnosticCenterId: String(record.diagnosticCenterId),
          serviceId: String(record.serviceId),
          examType: record.examType,
          examTypeOtherText: record.examTypeOtherText || '',
          orderReason: record.orderReason,
          orderReasonOtherText: record.orderReasonOtherText || '',
          showBillingSection: !!record.showBillingSection,
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message:
            err instanceof Error ? err.message : 'Failed to load requisition',
          severity: 'error',
        });
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [token, logout, id]);

  const handleSelectChange =
    (field: keyof typeof formData) => (event: SelectChangeEvent) => {
      setFormData({ ...formData, [field]: event.target.value });
      if (errors[field]) setErrors({ ...errors, [field]: '' });
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.diagnosticCenterId)
      newErrors.diagnosticCenterId = 'Diagnostic center is required';
    if (!formData.serviceId) newErrors.serviceId = 'Service is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !token || !id) return;
    setLoading(true);
    try {
      await centerServiceRequisitionsService.updateRequisition(
        token,
        Number(id),
        {
          diagnosticCenterId: Number(formData.diagnosticCenterId),
          serviceId: Number(formData.serviceId),
          // examType/orderReason are admin-configurable option strings, not
          // limited to the 3 well-known values RequisitionExamType/
          // RequisitionOrderReason enumerate for autocomplete - same cast
          // AddCenterServiceRequisition.tsx uses for the same reason.
          examType: formData.examType as RequisitionExamType,
          examTypeOtherText: formData.examTypeOtherText || undefined,
          orderReason: formData.orderReason as RequisitionOrderReason,
          orderReasonOtherText: formData.orderReasonOtherText || undefined,
          showBillingSection: formData.showBillingSection,
        }
      );
      setSnackbar({
        open: true,
        message: 'Requisition updated successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/center-service-requisitions'), 800);
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : 'Failed to update requisition',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography variant="h4">Edit Center Service Requisition</Typography>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.diagnosticCenterId}>
                <InputLabel>Diagnostic Center</InputLabel>
                <Select
                  value={formData.diagnosticCenterId}
                  label="Diagnostic Center"
                  onChange={handleSelectChange('diagnosticCenterId')}
                >
                  {centers.map(center => (
                    <MenuItem key={center.id} value={center.id}>
                      {center.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.serviceId}>
                <InputLabel>Service</InputLabel>
                <Select
                  value={formData.serviceId}
                  label="Service"
                  onChange={handleSelectChange('serviceId')}
                >
                  {services.map(service => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Exam Type</InputLabel>
                <Select
                  value={formData.examType}
                  label="Exam Type"
                  onChange={handleSelectChange('examType')}
                >
                  {examTypes.map(opt => (
                    <MenuItem key={opt.id} value={opt.name}>
                      {opt.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.examType === 'OTHER' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Exam Type (Other)"
                  value={formData.examTypeOtherText}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      examTypeOtherText: e.target.value,
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Order Reason</InputLabel>
                <Select
                  value={formData.orderReason}
                  label="Order Reason"
                  onChange={handleSelectChange('orderReason')}
                >
                  {orderReasons.map(opt => (
                    <MenuItem key={opt.id} value={opt.name}>
                      {opt.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.orderReason === 'OTHER' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Order Reason (Other)"
                  value={formData.orderReasonOtherText}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      orderReasonOtherText: e.target.value,
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.showBillingSection}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        showBillingSection: e.target.checked,
                      })
                    }
                    color="primary"
                  />
                }
                label="Show Billing Section in Requisition PDF"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    loading ? <CircularProgress size={18} /> : <Save />
                  }
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditCenterServiceRequisition;
